// MaterialGraphDetails.jsx — Right sidebar with preview, node inspector, + exposed params.
// Uses design's .sg-* classes.

import React, { useMemo } from 'react'
import MaterialPreview from './MaterialPreview'
import { NODE_TYPES } from '../../lib/materialNodes'
import { categoryOf, CAT_COLORS, pinColor } from './materialGraphTheme'
import { collectExposedParams } from '../../lib/materialGraphParams.js'
import { DEFAULT_CLIP } from '../../lib/graphBlendMode'
import './materialGraph.css'

// ─── Node Inspector ────────────────────────────────────────────────
function NodeInspector({ selectedNodeId, nodes }) {
  const node = nodes.find(n => n.id === selectedNodeId)
  if (!node) return null

  const registry = NODE_TYPES[node.data.nodeType]
  if (!registry) return null

  const category = categoryOf(node.data.nodeType)
  const catColor = CAT_COLORS[category] || CAT_COLORS.param

  return (
    <div>
      {/* Node title + category */}
      <div className="sg-inspect-head">
        <span className="sg-inspect-pin" style={{ background: catColor, color: catColor }} />
        <div>
          <div className="sg-inspect-title">{registry.label}</div>
          <div className="sg-inspect-sub">{node.id}</div>
        </div>
      </div>

      {/* Inputs section */}
      {registry.inputs && registry.inputs.length > 0 && (
        <div className="sg-inspect-sec">
          <div className="sg-inspect-seclbl">Inputs</div>
          {registry.inputs.map(input => {
            const color = pinColor(input.type)
            return (
              <div key={input.handle} className="sg-inspect-row">
                <span className="sg-inspect-pin" style={{ background: color, color: color }} />
                <span className="sg-inspect-name">{input.handle}</span>
                <span className="sg-inspect-type">{input.type}</span>
              </div>
            )
          })}
        </div>
      )}

      {/* Outputs section */}
      {registry.outputs && registry.outputs.length > 0 && (
        <div className="sg-inspect-sec">
          <div className="sg-inspect-seclbl">Outputs</div>
          {registry.outputs.map(output => {
            const color = pinColor(output.type)
            return (
              <div key={output.handle} className="sg-inspect-row">
                <span className="sg-inspect-pin" style={{ background: color, color: color }} />
                <span className="sg-inspect-name">{output.handle}</span>
                <span className="sg-inspect-type">{output.type}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Blend Mode (material-level, Unreal-style) ─────────────────────
// Edits live on the output node's data (blendMode / clip / twoSided) so the
// graph — and its preview — stay the single source of truth. Masked = opacity
// mask (hard cutout via alphaTest); wire the mask into the output `opacity` pin.
function BlendControls({ nodes, updateNodeData }) {
  const outNode = nodes.find(n => NODE_TYPES[n.data.nodeType]?.isOutput)
  if (!outNode || !updateNodeData) return null
  const d = outNode.data
  const mode = d.blendMode ?? 'opaque'
  const set = (handle, value) => updateNodeData(outNode.id, handle, value)

  return (
    <div className="sg-summary">
      <div className="sg-sum-row">
        <span>Blend</span>
        <select
          className="sg-select"
          value={mode}
          onChange={e => set('blendMode', e.target.value)}
          title="Masked cuts out where the opacity input is dark; Translucent blends"
        >
          <option value="opaque">Opaque</option>
          <option value="masked">Masked</option>
          <option value="translucent">Translucent</option>
        </select>
      </div>
      {mode === 'masked' && (
        <div className="sg-sum-row">
          <span>Clip</span>
          <input
            type="number" min={0} max={1} step={0.01}
            className="sg-val"
            value={d.clip ?? DEFAULT_CLIP}
            onChange={e => { const n = parseFloat(e.target.value); if (Number.isFinite(n)) set('clip', Math.min(1, Math.max(0, n))) }}
            title="Discard pixels whose opacity is below this value"
            style={{ width: 64 }}
          />
        </div>
      )}
      <label className="sg-bool" style={{ marginTop: 4 }}>
        <input
          type="checkbox"
          checked={!!d.twoSided}
          onChange={e => set('twoSided', e.target.checked)}
        />
        <span>two-sided</span>
      </label>
    </div>
  )
}

// ─── Material Summary ──────────────────────────────────────────────
function MaterialSummary({ nodes }) {
  const nodeCount = nodes.length

  return (
    <div className="sg-summary">
      <div className="sg-sum-row">
        <span>Nodes</span>
        <b>{nodeCount}</b>
      </div>
      <div className="sg-sum-row">
        <span>Shader</span>
        <b>Principled BSDF</b>
      </div>
      <div className="sg-sum-note">
        {nodeCount} node{nodeCount !== 1 ? 's' : ''} feeding the output shader.
      </div>
    </div>
  )
}

// ─── Exposed Parameters ────────────────────────────────────────────
function ExposedParameters({ liveGraph, nodes, onSelectNode }) {
  const exposed = useMemo(
    () => collectExposedParams(liveGraph),
    [liveGraph]
  )

  if (exposed.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--fg-3)', textAlign: 'center', padding: '8px 0' }}>
        No exposed parameters
      </div>
    )
  }

  return (
    <div className="sg-exposed">
      {exposed.map((param, i) => (
        <div
          key={i}
          className="sg-exp-row"
          onClick={() => onSelectNode(param.nodeId)}
        >
          <span className="sg-exp-val">
            {Array.isArray(param.value)
              ? `[${param.value.slice(0, 2).map(v => v.toFixed(2)).join(',')}]`
              : typeof param.value === 'number'
              ? param.value.toFixed(2)
              : '—'}
          </span>
          <span className="sg-exp-name">{param.name}</span>
          <span className="sg-exp-type">{param.type}</span>
        </div>
      ))}
    </div>
  )
}

// ─── Main Details Panel ───────────────────────────────────────────
export default function MaterialGraphDetails({
  graph,
  nodes,
  edges,
  selectedNodeId,
  onSelectNode,
  updateNodeData,
}) {
  const liveGraph = useMemo(() => {
    // Derive live graph from current nodes + edges (for preview + exposed list)
    if (!nodes) return { version: 1, nodes: [], edges: [] }
    return {
      version: 1,
      nodes: nodes.map(n => ({
        id: n.id,
        type: n.data.nodeType,
        data: (() => {
          const { nodeType, nodeId, ...inlineData } = n.data
          return inlineData
        })(),
        position: n.position,
      })),
      edges: (edges || []).map(e => ({
        id: e.id,
        source: e.source,
        sourceHandle: e.sourceHandle,
        target: e.target,
        targetHandle: e.targetHandle,
      })),
    }
  }, [nodes, edges])

  return (
    <div className="sg-details">
      {/* Preview sphere */}
      <div className="sg-detail-block">
        <div className="sg-detail-hd">Preview</div>
        <div className="sg-sphere-wrap">
          <MaterialPreview
            graph={liveGraph}
            assetName="material"
            embedded={true}
          />
        </div>
      </div>

      {/* Blend mode (material-level) — right under the preview, always visible */}
      <div className="sg-detail-block">
        <div className="sg-detail-hd">Blend</div>
        <BlendControls nodes={nodes} updateNodeData={updateNodeData} />
      </div>

      {/* Node inspector or material summary */}
      <div className="sg-detail-block">
        <div className="sg-detail-hd">{selectedNodeId ? 'Node' : 'Material'}</div>
        {selectedNodeId ? (
          <NodeInspector selectedNodeId={selectedNodeId} nodes={nodes} />
        ) : (
          <MaterialSummary nodes={nodes} />
        )}
      </div>

      {/* Exposed parameters */}
      <div className="sg-detail-block sg-detail-grow">
        <div className="sg-detail-hd">Exposed Parameters</div>
        <ExposedParameters
          liveGraph={liveGraph}
          nodes={nodes}
          onSelectNode={onSelectNode}
        />
      </div>
    </div>
  )
}
